

















































import { HTTPResponseCode } from '@/libs/HTTPResponseCodes'
import { computed, defineComponent, reactive, ref } from '@vue/composition-api'
import { useApi } from '@/composable/useApi'
import { useCrudChecker } from '@/composable/useCrudChecker'
import { useI18n } from 'vue-i18n-bridge'
import { useRoute } from '@/composable/migrateUseRoute'
import { useRouter } from '@intlify/vue-router-bridge'
import { useToasts } from '@/composable/useToasts'
import { useValidator } from '@/composable/validator/useValidator'
import DynamicGroupActionDelete from '@/components/domains/settings/dynamic/groups/actions/DynamicGroupActionDelete.vue'
import DynamicGroupInputs from '@/components/domains/settings/dynamic/groups/DynamicGroupsInputs.vue'
import ResourceHeader from '@/components/layouts/dashboard/ResourceHeader.vue'
import ScrollableResource from '@/components/layouts/dashboard/ScrollableResource.vue'
import type { DynamicGroup } from '@futureecom/futureecom-js/dist/services/setting-service'
import type { RuleDefinitions } from '@/types/validator'

export default defineComponent({
  name: 'DynamicGroupUpdate',

  components: {
    DynamicGroupActionDelete,
    DynamicGroupInputs,
    ResourceHeader,
    ScrollableResource
  },

  setup() {
    const router = useRouter()
    const route = useRoute()

    const { addSuccessToast, addErrorToast } = useToasts()
    const { canCreate, canUpdate, canDelete } = useCrudChecker('dynamic\\attribute')
    const { t } = useI18n()

    const api = useApi()

    const { id } = route.params

    const loading = reactive({
      get: false,
      update: false,
      delete: false
    })

    const item = ref<DynamicGroup>()

    const { errors, validate, isValid, setErrorsFromAPI, setModel } = useValidator()

    const rules = computed(
      (): RuleDefinitions => ({
        code: ['required', 'lower_alpha_floor'],
        label: ['required', 'string'],
        resource_types: ['required', { rule: 'min:1', message: t('At least one type is required') }]
      })
    )

    const onItemChange = () => item.value && validate(item.value, rules.value)

    const redirectToGrid = () => router.push({ name: 'dynamic-groups' })

    const onRequestError = (err: any) => {
      const { status, data } = err.response || {}
      if (status === HTTPResponseCode.NOT_FOUND) {
        addErrorToast('Resource not found')
        canCreate.value ? router.push({ name: 'dynamic-group-create' }) : redirectToGrid()
        return
      }
      if (status === HTTPResponseCode.UNPROCESSABLE_CONTENT) {
        setErrorsFromAPI(data.errors || {})
      }
      addErrorToast(data.message)
    }

    const updateHandler = () => {
      if (!item.value) {
        return
      }

      validate(item.value, rules.value, true)

      if (!isValid.value) {
        return
      }

      loading.update = true

      const { dynamic_attributes, ...rest } = item.value

      api.settings
        .updateDynamicGroup(id, rest)
        .then((val:any) => {
          addSuccessToast(t('Updated dynamic group'))
          item.value = val
          if (item.value) {
            setModel(item.value);
          }
        })
        .catch((err:any) => onRequestError(err))
        .finally(() => (loading.update = false))
    }

    api.settings
      .getDynamicGroup(id)
      .then((val:any) => {
        item.value = val
        if (item.value) {
          setModel(item.value);
        }
      })
      .catch((err:any) => onRequestError(err))
      .finally(() => (loading.get = false))

    return {
      canUpdate,
      canDelete,
      item,
      errors,
      isValid,
      loading,
      updateHandler,
      redirectToGrid,
      onItemChange,
      onRequestError
    }
  }
})
